import { useNavigate } from "@tanstack/react-router";
import React from "react";
import { shellBackgroundClass, settingsShellBackgroundClass } from "../shell-constants";

export type AppShellPageMeta = {
  title: string;
  subtitle: string;
};

export const NEW_DRAFT_REQUEST_EVENT = "fluentry:new-draft-request";

type RouteStaticData = AppShellPageMeta;

export function useAppShellRouteState(
  pathname: string,
  matches: readonly { staticData?: unknown }[],
) {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const pendingWorkbenchSectionRef = React.useRef<"drafts" | "history" | null>(null);
  const pendingHashFrameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    return () => {
      if (pendingHashFrameRef.current != null) {
        window.cancelAnimationFrame(pendingHashFrameRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (pathname !== "/" || !pendingWorkbenchSectionRef.current) {
      return;
    }

    const targetHash = pendingWorkbenchSectionRef.current;
    pendingWorkbenchSectionRef.current = null;

    // Re-applying the hash keeps the workbench page responsible for its own scroll/focus behavior after route changes.
    if (window.location.hash === `#${targetHash}`) {
      window.location.hash = "";
      pendingHashFrameRef.current = window.requestAnimationFrame(() => {
        window.location.hash = targetHash;
        pendingHashFrameRef.current = null;
      });
      return;
    }

    window.location.hash = targetHash;
  }, [pathname]);

  const handleNavigateToWorkbenchSection = React.useCallback(
    async (section: "drafts" | "history") => {
      pendingWorkbenchSectionRef.current = section;
      if (pathname !== "/") {
        await navigate({ to: "/" });
        return;
      }

      if (window.location.hash === `#${section}`) {
        window.location.hash = "";
        if (pendingHashFrameRef.current != null) {
          window.cancelAnimationFrame(pendingHashFrameRef.current);
        }
        pendingHashFrameRef.current = window.requestAnimationFrame(() => {
          window.location.hash = section;
          pendingHashFrameRef.current = null;
        });
        return;
      }

      window.location.hash = section;
    },
    [navigate, pathname],
  );

  const handleCreateDraft = React.useCallback(async () => {
    window.location.hash = "";
    if (pathname !== "/") {
      await navigate({ to: "/" });
    }
    window.dispatchEvent(new CustomEvent(NEW_DRAFT_REQUEST_EVENT));
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
  }, [navigate, pathname]);

  const routeMeta = matches
    .map((match) => match.staticData as RouteStaticData | undefined)
    .filter(Boolean)
    .at(-1);
  const isStandaloneRoute = pathname === "/login" || pathname === "/open";
  const isSettingsRoute = pathname.startsWith("/settings");
  const isUtilityRoute = pathname.startsWith("/settings") || pathname.startsWith("/help");
  const shellSurfaceClass = isUtilityRoute ? settingsShellBackgroundClass : shellBackgroundClass;
  const sectionLabel = pathname.startsWith("/settings")
    ? "Settings"
    : pathname.startsWith("/help")
      ? "Help"
      : "Workbench";
  const pageMeta = routeMeta ?? {
    title: "Workbench",
    subtitle: "A focused space for cross-language refinement",
  };

  return {
    pageMeta,
    shellSurfaceClass,
    isStandaloneRoute,
    isSettingsRoute,
    mobileNavOpen,
    setMobileNavOpen,
    sectionLabel,
    handleNavigateToWorkbenchSection,
    handleCreateDraft,
  };
}
